export type SecurityDisplayInput = {
  securityType?: string | null;
  description?: string | null;
  propertyAddress?: string | null;
  titleReference?: string | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleRegistrationNumber?: string | null;
  equipmentMake?: string | null;
  equipmentModel?: string | null;
  equipmentSerialNumber?: string | null;
  equipmentAssetTag?: string | null;
  financialAssetDescription?: string | null;
  financialAssetType?: string | null;
  financialAssetInstitutionName?: string | null;
};

const DASH = "-";

function clean(value?: string | null): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function joinParts(...parts: Array<string | null | undefined>): string | null {
  const values = parts.map(clean).filter((value): value is string => Boolean(value));
  return values.length > 0 ? values.join(" ") : null;
}

function humanizeSnakeCase(value?: string | null): string | null {
  const cleaned = clean(value);
  if (!cleaned) return null;
  return cleaned
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getSecurityDisplayName(security: SecurityDisplayInput): string {
  const description = clean(security.description);
  if (description) return description;

  switch (security.securityType) {
    case "residential_property":
    case "commercial_property":
      return (
        clean(security.propertyAddress) ??
        clean(security.titleReference) ??
        description ??
        DASH
      );

    case "vehicle":
      return (
        joinParts(security.vehicleMake, security.vehicleModel) ??
        clean(security.vehicleRegistrationNumber) ??
        DASH
      );

    case "equipment":
      return (
        joinParts(security.equipmentMake, security.equipmentModel) ??
        clean(security.equipmentAssetTag) ??
        clean(security.equipmentSerialNumber) ??
        DASH
      );

    case "financial_asset":
      return (
        clean(security.financialAssetDescription) ??
        clean(security.financialAssetInstitutionName) ??
        humanizeSnakeCase(security.financialAssetType) ??
        DASH
      );

    default:
      return DASH;
  }
}

export function getSecuritySecondaryText(
  security: SecurityDisplayInput,
  fallback = "No description"
): string {
  const description = clean(security.description);
  if (description) return description;

  const displayName = getSecurityDisplayName(security);
  if (displayName !== DASH) {
    return displayName;
  }

  return fallback;
}
